import { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Menu, MenuItem, Box, Typography, Avatar, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { PersonOutline, KeyboardArrowDown, NotificationsNone, Logout } from '@mui/icons-material';
import { toggleSidebarMini } from '../../Redux/Reducers/sidebarReducer';
import { fetchUserNotifications, markAllNotificationsAsRead, markNotificationAsRead } from '../../Redux/Reducers/UserNotificationsReducer';
import enFlag from '../../assets/images/flag/GB.png';
import frFlag from '../../assets/images/flag/FR.png';
import { getFullUrl } from '../../utils/globalUtils';
import axiosInstance from '../../utils/axiosInstance';
import toast from 'react-hot-toast';
import { isNotificationRead, getNotificationId, getNotificationRedirectUrl } from '../../utils/notificationUtils';

function Header(props) {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [profileAnchorEl, setProfileAnchorEl] = useState(null);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const navigate = useNavigate();
    const openLangMenu = Boolean(anchorEl);
    const openProfileMenu = Boolean(profileAnchorEl);

    const user = useSelector((state) => state?.AuthReducer?.user || state?.auth?.user);
    const token = useSelector((state) => state?.AuthReducer?.token || state?.auth?.token);

    const getRoleName = () => {
        const rawRole =
            user?.roleName ||
            (typeof user?.role === 'string' ? user.role : user?.role?.name || user?.role?.roleName) ||
            'Super Admin';

        const normalized = String(rawRole).trim().toLowerCase();

        if (normalized === 'super admin' || normalized === 'superadmin' || normalized === 'super_administrator') {
            return t('role_super_admin', 'Super Admin');
        }

        if (normalized === 'admin' || normalized === 'administrator') {
            return t('role_admin', 'Admin');
        }

        return rawRole;
    };

    const getDisplayName = () => {
        const rawName = user?.name || 'User';
        const normalized = String(rawName).trim().toLowerCase();

        if (normalized === 'super admin' || normalized === 'superadmin' || normalized === 'super_administrator') {
            return t('role_super_admin', 'Super Admin');
        }

        if (normalized === 'admin' || normalized === 'administrator') {
            return t('role_admin', 'Admin');
        }

        return rawName;
    };

    const handleLangClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLangClose = async (lng) => {
        if (lng) {
            try {
                await i18n.changeLanguage(lng);
                await axiosInstance.put('/api/admin/auth/user/language', { language: lng });
                // The API translates notifications using the saved user language.
                // Refresh as soon as the preference is persisted instead of waiting
                // for App's background polling interval.
                dispatch(fetchUserNotifications({ type: 'all', page: 1, limit: 10 }));
            } catch (error) {
                console.error('Language update failed', error);
            }
        }
        setAnchorEl(null);
    };

    const handleProfileClick = (event) => {
        setProfileAnchorEl(event.currentTarget);
    };

    const handleProfileClose = () => {
        setProfileAnchorEl(null);
    };

    const handleLogoutClick = () => {
        handleProfileClose();
        setLogoutDialogOpen(true);
    };

    const handleConfirmLogout = async () => {
        setLogoutDialogOpen(false);
        try {
            const response = await axiosInstance.post('/api/admin/auth/logout');
            toast.success(response.data?.message || 'Logged out successfully');
        } catch (err) {
            console.error('Logout error:', err);
            toast.error(err.response?.data?.message || 'Logout failed');
        } finally {
            sessionStorage.clear();
            document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            setTimeout(() => {
                window.location.href = '/login';
            }, 500);
        }
    };

    useEffect(() => {
        if (!token) {
            return;
        }

        dispatch(fetchUserNotifications({ type: 'all', page: 1, limit: 10 }));
    }, [dispatch, token]);

    // Notification 
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const openNotificationMenu = Boolean(notificationAnchorEl);

    const notifications = useSelector((state) => state.userNotifications?.notifications || []);
    const unreadCount = useSelector((state) => state.userNotifications?.unreadCount || 0);
    const loadingNotifications = useSelector((state) => state.userNotifications?.loading || false);

    const handleNotificationClick = (event) => {
        setNotificationAnchorEl(event.currentTarget);
        if (!notifications.length && !loadingNotifications) {
            dispatch(fetchUserNotifications({ type: 'all', page: 1, limit: 10 }));
        }
    };
    const handleNotificationClose = () => {
        setNotificationAnchorEl(null);
    };

    const handleNotificationItemClick = async (notification) => {
        const notificationId = getNotificationId(notification);

        if (notificationId && !isNotificationRead(notification)) {
            try {
                await dispatch(markNotificationAsRead({ id: notificationId })).unwrap();
            } catch (error) {
                console.log('MARK NOTIFICATION READ ERROR:', error);
            }
        }

        const redirectUrl = getNotificationRedirectUrl(notification, user);

        handleNotificationClose();

        if (redirectUrl) {
            navigate(redirectUrl);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await dispatch(markAllNotificationsAsRead()).unwrap();
            dispatch(fetchUserNotifications({ type: 'all', page: 1, limit: 10 }));
        } catch (error) {
            console.log('MARK ALL NOTIFICATIONS READ ERROR:', error);
        }
    };

    const handleViewAll = () => {
        handleNotificationClose();
        navigate('/notifications?type=all&page=1');
    };


    return (
        <div className="header">
            <nav className="navbar">
                <div className="d-flex justify-content-between align-items-center w-100 px-4">
                    {/* <Link to="/dashboard"></Link> */}
                    <div className="d-flex align-items-center">
                        {/* Sidebar Mini Toggle Button */}
                        <button
                            type="button"
                            className="btn btn-link sidebar-mini-btn p-0 border-0"
                            onClick={() => dispatch(toggleSidebarMini())}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                                <path d="M26.5855 21.25C27.2977 21.25 27.875 21.8096 27.875 22.5C27.875 23.1904 27.2977 23.75 26.5855 23.75H4.66447C3.95232 23.75 3.375 23.1904 3.375 22.5C3.375 21.8096 3.95232 21.25 4.66447 21.25H26.5855ZM26.5855 13.75C27.2977 13.75 27.875 14.3096 27.875 15C27.875 15.6904 27.2977 16.25 26.5855 16.25H4.66447C3.95232 16.25 3.375 15.6904 3.375 15C3.375 14.3096 3.95232 13.75 4.66447 13.75H26.5855ZM17.1902 6.25C17.9024 6.25 18.4797 6.80964 18.4797 7.5C18.4797 8.19036 17.9024 8.75 17.1902 8.75H4.66447C3.95232 8.75 3.375 8.19036 3.375 7.5C3.375 6.80964 3.95232 6.25 4.66447 6.25H17.1902Z" fill="white" />
                            </svg>
                        </button>

                        {/* Mobile Menu Toggle */}
                        <button
                            className="navbar-toggler p-0 border-0 menu-toggle"
                            type="button"
                            onClick={() => {
                                const sidebar = document.getElementById('mainsidemenu');
                                const overlay =
                                    document.getElementById('sidebar-overlay');
                                if (sidebar) {
                                    sidebar.classList.toggle('open');
                                    if (overlay) {
                                        if (sidebar.classList.contains('open')) {
                                            overlay.classList.add('active');
                                        } else {
                                            overlay.classList.remove('active');
                                        }
                                    }
                                }
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                                <path d="M26.5855 21.25C27.2977 21.25 27.875 21.8096 27.875 22.5C27.875 23.1904 27.2977 23.75 26.5855 23.75H4.66447C3.95232 23.75 3.375 23.1904 3.375 22.5C3.375 21.8096 3.95232 21.25 4.66447 21.25H26.5855ZM26.5855 13.75C27.2977 13.75 27.875 14.3096 27.875 15C27.875 15.6904 27.2977 16.25 26.5855 16.25H4.66447C3.95232 16.25 3.375 15.6904 3.375 15C3.375 14.3096 3.95232 13.75 4.66447 13.75H26.5855ZM17.1902 6.25C17.9024 6.25 18.4797 6.80964 18.4797 7.5C18.4797 8.19036 17.9024 8.75 17.1902 8.75H4.66447C3.95232 8.75 3.375 8.19036 3.375 7.5C3.375 6.80964 3.95232 6.25 4.66447 6.25H17.1902Z" fill="white" />
                            </svg>
                        </button>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            type='button'
                            onClick={handleNotificationClick}
                            sx={{
                                border: '1px solid rgba(230, 210, 249, 0.1) !important',
                                color: '#fff',
                                textTransform: 'none',
                                backgroundColor: 'rgba(230, 210, 249, 0.2) !important',
                                borderRadius: '999px',
                                minWidth: '42px',
                                minHeight: '42px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                            }}
                        >
                            <img src="/assets/images/notifications-icon.svg" alt="flag" />
                            {unreadCount > 0 && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: -4,
                                        right: -4,
                                        minWidth: 18,
                                        height: 18,
                                        px: "4px",
                                        borderRadius: "999px",
                                        background: "#FF4D4F",
                                        color: "#fff",
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {unreadCount}
                                </Box>
                            )}
                        </Button>
                        {/* Notification Dropdown */}
                        <Menu
                            anchorEl={notificationAnchorEl}
                            open={openNotificationMenu}
                            onClose={handleNotificationClose}
                            transformOrigin={{
                                horizontal: "right",
                                vertical: "top",
                            }}
                            anchorOrigin={{
                                horizontal: "right",
                                vertical: "bottom",
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        width: 360,
                                        mt: 1.5,
                                        overflow: "hidden",
                                        borderRadius: "4px",
                                        background: "#2e2a32",
                                        border: "1px solid rgba(230, 210, 249, 0.1)",
                                        boxShadow: "0px 10px 40px rgba(0,0,0,0.45)",
                                        color: "#fff",
                                    },
                                },
                            }}
                            sx={{
                                "& .MuiMenu-list": {
                                    padding: 0,
                                },
                            }}
                        >
                            {/* Header */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.8,
                                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1.5,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 700,
                                    }}
                                >
                                    {t('header.notifications.title', "Notifications")}
                                </Typography>
                                <Button
                                    type="button"
                                    onClick={handleMarkAllRead}
                                    disabled={!notifications.length || unreadCount === 0}
                                    sx={{
                                        minWidth: 'auto',
                                        p: 0,
                                        textTransform: 'none',
                                        color: '#ff69b4',
                                        fontWeight: 600,
                                        fontSize: 12,
                                        '&.Mui-disabled': {
                                            color: 'rgba(255,255,255,0.3)',
                                        },
                                    }}
                                >
                                    {t('header.notifications.mark_all_read', "Mark all read")}
                                </Button>
                            </Box>

                            {/* Notifications List */}
                            <Box
                                sx={{
                                    maxHeight: 400,
                                    overflowY: "auto",
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'rgba(230, 210, 249, 0.3)',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            background: 'rgba(230, 210, 249, 0.5)',
                                        },
                                    },
                                }}>
                                {notifications.length > 0 ? notifications.map((item) => {
                                    const notificationId = getNotificationId(item);
                                    const unread = !isNotificationRead(item);
                                    const title = item?.title || item?.heading || item?.subject || 'Notification';
                                    const description = item?.description || item?.message || item?.body || '';
                                    const time = item?.time || item?.createdAt || item?.created_at || item?.sentAt || item?.sent_at || '';

                                    return (
                                        <MenuItem
                                            key={notificationId || title}
                                            onClick={() => handleNotificationItemClick(item)}
                                            sx={{
                                                alignItems: "flex-start",
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.8,
                                                whiteSpace: "normal",
                                                borderBottom: "1px solid rgba(255,255,255,0.04)",
                                                transition: "all .2s ease",
                                                background: unread ? "rgba(255,255,255,0.01)" : "transparent",
                                                "&:hover": {
                                                    background:
                                                        "rgba(255,255,255,0.05)",
                                                },
                                            }}
                                        >
                                            {/* Left Icon */}
                                            <Box
                                                sx={{
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: "50%",
                                                    background: "rgba(179, 136, 255, 0.12)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <NotificationsNone
                                                    sx={{
                                                        fontSize: 20,
                                                        color: "#ff69b4",
                                                    }}
                                                />
                                            </Box>
                                            {/* Content */}
                                            <Box sx={{ flex: '0 0 calc(100% - 50px)', maxWidth: 'calc(100% - 50px)' }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                            color: "#fff",
                                                            pr: 1,
                                                        }}
                                                    >
                                                        {title}
                                                    </Typography>
                                                    {unread && (
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: "50%",
                                                                background: "#ff69b4",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        color:
                                                            "rgba(255,255,255,0.65)",
                                                        mt: 0.4,
                                                        lineHeight: "20px",
                                                    }}
                                                >
                                                    {description}
                                                </Typography>
                                                {time ? (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: "rgba(255,255,255,0.45)",
                                                            mt: 1,
                                                        }}
                                                    >
                                                        {time}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </MenuItem>
                                    );
                                }) : (
                                    <Box sx={{ px: 2, py: 3, color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                                        {t('header.notifications.no_notification', "No notifications yet.")}
                                    </Box>
                                )}
                            </Box>

                            {/* Footer */}
                            {/* <Box sx={{ p: 1.5, }}>
                                <Button
                                    fullWidth
                                    onClick={handleViewAll}
                                    sx={{
                                        textTransform: "none",
                                        color: "#ff69b4",
                                        fontWeight: 600,
                                        p: 0,
                                        background: "transparent",
                                        '&:hover': {
                                            background: "transparent",
                                            textDecoration: "underline",
                                        }
                                    }}
                                >
                                    View All
                                </Button>
                            </Box> */}
                        </Menu>


                        <Button
                            onClick={handleLangClick}
                            variant="outlined"
                            size="small"
                            startIcon={
                                <img
                                    src={i18n.language.startsWith('fr') ? frFlag : enFlag}
                                    alt="flag"
                                    style={{ width: '20px', borderRadius: '2px' }}
                                />
                            }
                            sx={{
                                border: '1px solid rgba(230, 210, 249, 0.1) !important',
                                color: '#fff',
                                textTransform: 'none',
                                backgroundColor: 'rgba(230, 210, 249, 0.2) !important',
                                borderRadius: '999px',
                                fontSize: '15px',
                                lineHeight: '24px',
                                minWidth: '80px',
                                padding: '8px 8px',
                            }}
                        >
                            {i18n.language.toUpperCase().substring(0, 2)}
                        </Button>

                        <Button
                            onClick={handleProfileClick}
                            sx={{
                                border: '1px solid rgba(230, 210, 249, 0.1) !important',
                                color: '#fff',
                                textTransform: 'none',
                                backgroundColor: 'rgba(230, 210, 249, 0.2) !important',
                                borderRadius: '999px',
                                fontSize: '15px',
                                lineHeight: '24px',
                                minWidth: '80px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <Box sx={{
                                width: 30,
                                height: 30,
                                borderRadius: '5px',
                                border: '1px solid rgba(255, 255, 255, 0.4)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: '0',
                                overflow: 'hidden'
                            }}>
                                <Avatar
                                    variant="square"
                                    src={getFullUrl(user?.profileImage || null)}
                                    sx={{ width: 30, height: 30 }}
                                />
                                {/* <PersonOutline sx={{ fontSize: 18, color: '#fff' }} /> */}
                            </Box>
                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: '4px' }}>
                                <Typography sx={{ fontSize: '15px', fontWeight: 500, color: '#fff' }}>
                                    {getDisplayName()}
                                </Typography>
                                <Typography sx={{ fontSize: '15px', fontWeight: 500, color: '#fff' }}>
                                    ({getRoleName()})
                                </Typography>
                            </Box>
                            <KeyboardArrowDown sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                        </Button>

                        <Menu
                            anchorEl={profileAnchorEl}
                            open={openProfileMenu}
                            onClose={handleProfileClose}
                            slotProps={{
                                paper: {
                                    sx: {
                                        border: '1px solid rgba(230, 210, 249, 0.1)',
                                        backgroundColor: '#2e2a32',
                                        borderRadius: '4px',
                                        mt: '10px',
                                        color: '#fff',
                                        padding: '0 !important',
                                        fontSize: '15px',
                                        lineHeight: '24px'
                                    },
                                },
                            }}
                            sx={{
                                '& .MuiMenu-list': {
                                    padding: 0,
                                },
                                '& .MuiMenuItem-root': {
                                    gap: '10px',
                                    py: '8px',
                                    px: '16px',
                                    minWidth: '150px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(230, 210, 249, 0.1)',
                                    }
                                }
                            }}
                        >
                            <MenuItem onClick={() => { handleProfileClose(); navigate('/profile'); }}>
                                <PersonOutline sx={{ fontSize: 20, mr: 0, color: 'rgba(255, 255, 255, 0.7)' }} />
                                {t('profile', 'Profile')}
                            </MenuItem>
                            <MenuItem onClick={handleLogoutClick}>
                                <Logout sx={{ fontSize: 20, mr: 0, color: 'rgba(255, 255, 255, 0.7)' }} />
                                {t('logout', 'Sign Out')}
                            </MenuItem>
                        </Menu>

                        <Menu
                            anchorEl={anchorEl}
                            open={openLangMenu}
                            onClose={() => handleLangClose()}
                            slotProps={{
                                paper: {
                                    sx: {
                                        border: '1px solid rgba(230, 210, 249, 0.1)',
                                        backgroundColor: '#2e2a32',
                                        borderRadius: '4px',
                                        mt: '10px',
                                        color: '#fff',
                                        padding: '0 !important',
                                        fontSize: '15px',
                                        lineHeight: '24px'
                                    },
                                },
                            }}
                            sx={{
                                '& .MuiMenu-list': {
                                    padding: 0,
                                },
                                '& .MuiMenuItem-root': {
                                    gap: '10px',
                                    py: '8px',
                                    px: '16px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(230, 210, 249, 0.1)',
                                    }
                                }
                            }}
                        >
                            <MenuItem onClick={() => handleLangClose('en')}>
                                <img src={enFlag} alt="EN" style={{ width: '20px', borderRadius: '2px' }} />
                                English
                            </MenuItem>
                            <MenuItem onClick={() => handleLangClose('fr')}>
                                <img src={frFlag} alt="FR" style={{ width: '20px', borderRadius: '2px' }} />
                                Français
                            </MenuItem>
                        </Menu>
                    </div>
                </div>
            </nav>

            {/* Logout Confirmation Dialog */}
            <Dialog
                open={logoutDialogOpen}
                onClose={() => setLogoutDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: '#252525',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        borderRadius: '20px',
                        minWidth: '350px'
                    }
                }}
            >
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center', color: '#fff' }}>
                    {t('confirm_logout', 'Confirm Logout')}
                </DialogTitle>
                <DialogContent sx={{ pb: 0 }}>
                    <Typography sx={{ padding: '0 16px 0', fontSize: '15px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
                        {t('logout_confirmation_message', 'Are you sure you want to logout?')}
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: '16px',
                        py: '20px',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '6px',
                    }}
                >
                    <Button
                        onClick={() => setLogoutDialogOpen(false)}
                        sx={{
                            backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
                            border: '1px solid rgba(227, 214, 245, 0.1)',
                            fontSize: '15px',
                            lineHeight: '20px',
                            fontWeight: '400',
                            padding: '11px 20px',
                            minWidth: { xs: '100%', sm: '150px' },
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '12px'
                        }}
                    >
                        {t('cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmLogout}
                        sx={{
                            backgroundColor: '#ff4d4f',
                            backgroundImage: 'linear-gradient(221deg, #FF1DC3 0%, #B720D9 100%)',
                            fontSize: '15px',
                            lineHeight: '20px',
                            fontWeight: '500',
                            padding: '11px 20px',
                            minWidth: { xs: '100%', sm: '150px' },
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '12px'
                        }}
                    >
                        {t('logout', 'Logout')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

const mapStateToProps = ({ Mainreducer }) => ({
    Mainreducer
});

export default connect(mapStateToProps, {})(Header);
